/**
 * ThreadPanel — Slack-style thread view
 *
 * Desktop: 400px sliding side panel.
 * Mobile: Used inside ThreadActivity as fullscreen.
 *
 * Shows root message at top, scrollable replies, input at bottom.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useAuthStore } from '@/features/auth';
import { useThreadReplies } from '../hooks';
import { useChat } from '../hooks';
import { MessageBubble } from './MessageBubble';
import { chatHaptic } from '../utils/chatHaptic';
import type { ChatMessage, WsMessageEvent } from '../types';

interface ThreadPanelProps {
  conversationId: string;
  rootMessage: ChatMessage;
  onClose: () => void;
}

export const ThreadPanel = memo(function ThreadPanel({
  conversationId,
  rootMessage,
  onClose,
}: ThreadPanelProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [localReplies, setLocalReplies] = useState<ChatMessage[]>([]);
  const repliesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useThreadReplies(conversationId, rootMessage.id);

  const allReplies = useMemo(() => {
    const serverReplies = data?.replies || [];
    // Merge server replies with any local WS replies (dedup by id)
    const ids = new Set(serverReplies.map(r => r.id));
    const merged = [...serverReplies, ...localReplies.filter(r => !ids.has(r.id))];
    return merged.sort((a, b) => a.sequenceId - b.sequenceId);
  }, [data?.replies, localReplies]);

  // WS: listen for new thread replies
  const handleWsMessage = useCallback((event: WsMessageEvent) => {
    if (event.threadRootId !== rootMessage.id) return;
    const newReply: ChatMessage = {
      id: event.id,
      conversationId,
      senderId: event.senderId,
      sequenceId: event.sequenceId,
      type: event.messageType,
      content: event.content || null,
      mediaUrl: event.mediaUrl || null,
      mediaThumbnail: event.mediaThumbnail || null,
      mediaMetadata: null,
      replyTo: event.replyTo || null,
      mentions: event.mentions || null,
      threadRootId: event.threadRootId,
      isEdited: false,
      editedAt: null,
      isDeleted: false,
      createdAt: new Date(event.timestamp).toISOString(),
      sender: {
        id: event.senderId,
        name: event.senderName,
        avatar: event.senderAvatar,
      },
      reactions: {},
      isStarred: false,
    };
    setLocalReplies(prev => [...prev, newReply]);
    if (event.senderId !== currentUserId) {
      chatHaptic.receive();
    }
  }, [conversationId, rootMessage.id, currentUserId]);

  const { sendText, isConnected } = useChat({
    conversationId,
    enabled: true,
    onMessage: handleWsMessage,
  });

  // Input
  const [inputText, setInputText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed) return;
    chatHaptic.send();
    sendText(trimmed, rootMessage.id);
    setInputText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [inputText, sendText, rootMessage.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Auto-scroll on new replies
  useEffect(() => {
    repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allReplies.length]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="flex h-full w-[400px] flex-shrink-0 flex-col border-s border-white/20 glass-surface"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/20 px-4 py-3">
        <h3 className="text-sm font-semibold text-neutral-800">{t('messaging.thread')}</h3>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/50 transition-all hover:bg-white/70 active:scale-95"
        >
          <svg className="h-4 w-4 text-neutral-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Root message */}
      <div className="border-b border-white/10 px-2 py-2">
        <MessageBubble
          message={rootMessage}
          showSender
          showAvatar
          isGrouped={false}
          onReply={() => {}}
          onReaction={() => {}}
          onDelete={() => {}}
          onEdit={() => {}}
          onStar={() => {}}
          onRetry={() => {}}
          onRemoveOptimistic={() => {}}
          onLongPress={() => {}}
          onMediaTap={() => {}}
        />
      </div>

      {/* Replies */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="h-5 w-5 animate-spin rounded-full border border-white/20 border-t-slate-800" />
          </div>
        )}

        {allReplies.map((reply) => (
          <MessageBubble
            key={reply.id}
            message={reply}
            showSender
            showAvatar
            isGrouped={false}
            onReply={() => {}}
            onReaction={() => {}}
            onDelete={() => {}}
            onEdit={() => {}}
            onStar={() => {}}
            onRetry={() => {}}
            onRemoveOptimistic={() => {}}
            onLongPress={() => {}}
            onMediaTap={() => {}}
          />
        ))}

        <div ref={repliesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-white/20 px-3 py-2 pb-safe">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
              if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('messaging.replyInThread')}
            disabled={!isConnected}
            rows={1}
            dir="auto"
            className="flex-1 resize-none rounded-xl border border-white/20 bg-white/80 px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-500 transition-all focus:bg-white/90 focus:outline-none disabled:opacity-50"
            style={{ maxHeight: 120 }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!inputText.trim() || !isConnected}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-slate-800 text-white shadow-sm transition-all hover:-translate-y-[1px] active:translate-y-[1px] disabled:opacity-40"
          >
            <svg className="h-5 w-5 -rotate-45 rtl:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
});
